import { useEffect, useState } from "react";
import { useLang } from "@/context/LanguageContext";

/**
 * Contemplative loader — a sun in the centre with planets quietly orbiting.
 * Used instead of blank skeletons while LLM-backed pages fetch.
 */
const TAGLINES_EN = [
  "Listening to the sky…",
  "Reading your chapter…",
  "Drawing the orbits…",
  "Letting the moment arrive…",
  "Walking with the planets…",
];
const TAGLINES_HI = [
  "आकाश को सुन रहे हैं…",
  "आपका अध्याय पढ़ रहे हैं…",
  "कक्षाएँ रच रहे हैं…",
  "क्षण को आने दीजिए…",
  "ग्रहों के साथ चल रहे हैं…",
];

export default function CelestialLoader({ minHeight = "60vh", label }) {
  const { lang } = useLang();
  const taglines = lang === "hi" ? TAGLINES_HI : TAGLINES_EN;
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % taglines.length), 2800);
    return () => clearInterval(t);
  }, [taglines.length]);

  return (
    <div
      className="w-full flex flex-col items-center justify-center"
      style={{ minHeight }}
      data-testid="celestial-loader"
      aria-busy="true"
      aria-live="polite"
    >
      <div className="relative w-[140px] h-[140px] flex items-center justify-center" style={{ perspective: "600px" }}>
        {/* faint orbit rings */}
        {[44, 60, 74].map((r, i) => (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              width: r * 2, height: r * 2,
              border: `1px solid rgba(184,149,74,${0.10 - i * 0.02})`,
            }}
          />
        ))}

        {/* sun at centre */}
        <div
          className="absolute rounded-full"
          style={{
            width: 22, height: 22,
            backgroundColor: "var(--vedari-gold)",
            boxShadow: "0 0 24px rgba(184,149,74,0.55), 0 0 8px rgba(184,149,74,0.8)",
            animation: "vedari-sun-pulse 3.2s ease-in-out infinite",
          }}
        />

        {/* orbiting planets — each on its own orbit, different speeds */}
        <Orbit radius={44} duration="6s" size={6} color="var(--vedari-sage)" delay="0s" />
        <Orbit radius={60} duration="10s" size={7} color="var(--vedari-amber)" delay="-1s" reverse />
        <Orbit radius={74} duration="16s" size={5} color="var(--vedari-slate)" delay="-3s" />
      </div>

      <p
        key={idx}
        className="font-serif italic text-body-sm mt-8 tracking-wide animate-soft-fade"
        style={{ color: "var(--vedari-ink-soft)" }}
        data-testid="celestial-loader-tagline"
      >
        {label || taglines[idx]}
      </p>
    </div>
  );
}

function Orbit({ radius, duration, size, color, delay = "0s", reverse = false }) {
  return (
    <div
      className="absolute inset-0 flex items-center justify-center"
      style={{
        animation: `vedari-orbit ${duration} linear ${reverse ? "reverse" : "normal"} infinite`,
        animationDelay: delay,
      }}
    >
      <div
        className="absolute rounded-full"
        style={{
          width: size, height: size,
          backgroundColor: color,
          transform: `translateX(${radius}px)`,
          boxShadow: `0 0 8px ${color}`,
        }}
      />
    </div>
  );
}
